import { CapabilityNav, PageHeader } from "@velocity-ehs/component-library";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getNavItems, LIST_MANAGER_PATH } from "./NavHelpers";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import ListsAccordion from "./ListsAccordion";
import AddListItemSlider from "./AddListItemSlider";
import UpdateListItemSlider from "./UpdateListItemSlider";
import { useState } from "react";
import useEhsListItemMutaion, {
  EhsListItemActions,
} from "../../../hooks/useEhsListItemMutation";

export interface EhsListItem {
  id?: string;
  listType?: string;
  listTypeCode: string;
  item: string;
  code: string;
  shortName: string;
  description: string;
  listOrder: number;
  enabled: boolean;
}

export interface EhsListSelection {
  name: string;
  code: string;
}

const EhsListsManagement: React.FC = () => {
  const { t } = useTranslation();
  const allFlags = useFlags();
  const navItems = getNavItems(UsersService.permissions, allFlags);
  const current = navItems.find((item) => item.id === LIST_MANAGER_PATH);

  const { mutations } = useEhsListItemMutaion(UsersService.customer.id);
  const [addSlider, setAddSlider] = useState({
    show: false,
    listType: "",
    listTypeCode: "",
  });
  const [showUpdateSlider, setShowUpdateSlider] = useState(false);
  const [selectedLists, setSelectedLists] = useState<EhsListSelection[]>([]);
  const [selectedListItem, setSelectedListItem] = useState<EhsListItem>(null);

  const onUpdate = async (item: EhsListItem) => {
    await mutations.mutate(EhsListItemActions.UPDATE, item);
  };

  const onAdd = async (item: EhsListItem) => {
    await mutations.mutate(EhsListItemActions.CREATE, item);
  };

  const onStatusChange = async (item: EhsListItem) => {
    const action = item.enabled
      ? EhsListItemActions.INACTIVATE
      : EhsListItemActions.ACTIVATE;
    await mutations.mutate(action, {
      ...item,
      enabled: !item.enabled,
    });
  };

  const onListOpen = (lists: EhsListSelection[]) => {
    setSelectedLists(lists);
  };

  const onOpenEdit = (item: EhsListItem) => {
    setShowUpdateSlider(true);
    setSelectedListItem({
      ...item,
      listType:
        selectedLists.find((list) => list.code === item.listTypeCode)?.name ||
        "",
    });
  };

  const onOpenAdd = (event: any, list: any) => {
    if (!event.isDefaultPrevented()) {
      event.preventDefault();
    }

    setAddSlider({
      show: true,
      listType: list.listType,
      listTypeCode: list.listTypeCode,
    });
  };

  return (
    <div className="App">
      <CapabilityNav
        capability="EHS Configurations"
        navItems={navItems}
        featureFlags={allFlags}
      />
      <div className="mainContent">
        <PageHeader
          title={current?.label || ""}
          navigationTree={[
            { text: `Accelerate ${t("universal_Home")}`, href: "/" },
            {
              text: `EHS ${t("ehs_configurations")}`,
              href: `/ehs-configurations`,
            },
            { text: t("list_manager_page_title"), href: "" },
          ]}
        />
        <AddListItemSlider
          showSlider={addSlider}
          closeSlider={() =>
            setAddSlider({ show: false, listType: "", listTypeCode: "" })
          }
          addListItem={onAdd}
        />
        <UpdateListItemSlider
          listItem={selectedListItem}
          showSlider={showUpdateSlider}
          closeSlider={() => setShowUpdateSlider(false)}
          updateListItem={onUpdate}
        />
        <ListsAccordion
          onListOpen={onListOpen}
          onOpenAdd={onOpenAdd}
          onOpenEdit={onOpenEdit}
          onStatusChange={onStatusChange}
        />
      </div>
    </div>
  );
};

export default EhsListsManagement;
